import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { getPayrollData, savePayrollForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import { trimData } from '../../../../../../utils/formatText';
import SkeletonBlock from '../../../SkeletonBlock';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { PAYROLL } from '../../../formQuestionsEnum';
import PayrollForm from './PayrollForm';

const PayrollView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const payrollPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.payrollFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getPayrollData(id, facilityId ?? 0)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(PAYROLL).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [setFormData, data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await savePayrollForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('Payroll data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save Payroll data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? <SkeletonBlock height={600} sx={{ mt: 2 }} /> : <PayrollForm permissions={payrollPermissions} />;
});

export default PayrollView;

PayrollView.propTypes = {
  setFormData: PropTypes.func,
};
